import { useQuery } from '@tanstack/react-query';
import { GetQueueViewCountsParams, QueueViewCountsResponse } from '@hone-automation/common';
import { makeRemoteGetQueueRequestsCount } from 'main/factories/usecases/queue';

const queueRequestsCount = makeRemoteGetQueueRequestsCount();

/**
 * Hook to fetch queue request counts by view
 *
 * @param params.locationId - The location ID to fetch counts for
 * @returns Query result with QueueViewCountsResponse containing counts per QueueView
 */
export const useGetQueueRequestsCountQuery = ({ locationId }: Partial<GetQueueViewCountsParams>) => {
  const hasValidLocationId = Boolean(locationId && locationId.trim().length > 0);

  return useQuery<QueueViewCountsResponse>({
    queryKey: ['queueRequestsCount', locationId],
    staleTime: 5000,
    queryFn: async (): Promise<QueueViewCountsResponse> => {
      if (!locationId) {
        throw new Error('No locationId available');
      }

      const response = await queueRequestsCount.get({ locationId });

      if (response && typeof response === 'object' && 'error' in response) {
        throw new Error(String((response as { error: unknown }).error));
      }

      return response as QueueViewCountsResponse;
    },
    enabled: hasValidLocationId,
  });
};
